/**
 * Редактор вида списка или отчёта (АН-09, АН-05): колонки, отбор, группировка, сортировка, итоги, вид,
 * период. Один и тот же для настройки страницы-списка и для конструктора отчётов. Кнопки «выше» и «ниже» —
 * крупные, чтобы на телефоне порядок колонок менялся без перетаскивания.
 */
import {
  AGG_FNS,
  type AggFn,
  type FilterOp,
  type SourceField,
  type SourceInfo,
  VIEW_KINDS,
  type ViewConfig,
  type ViewFilter,
} from '@epc/contracts';
import { ArrowDown, ArrowUp, Plus, X } from 'lucide-react';
import type { ReactNode } from 'react';
import { Button } from '../../ui/button';
import { Checkbox, Field, Input, Select } from '../../ui/form';
import { aggFnsFor, editorColumns, moveColumn, newFilter, opsFor, opTitle, viewKindsFor } from './util';

const ctl = 'h-11 md:h-10';

function Section({ title, hint, children }: { title: string; hint?: ReactNode; children: ReactNode }) {
  return (
    <section className="flex flex-col gap-2 border-b border-line pb-4 last:border-b-0 last:pb-0">
      <h3 className="text-[14px] font-semibold">{title}</h3>
      {hint ? <p className="text-[12px] text-ink-2">{hint}</p> : null}
      {children}
    </section>
  );
}

const IconBtn = ({
  label,
  onClick,
  disabled,
  children,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  children: ReactNode;
}) => (
  <button
    type="button"
    aria-label={label}
    title={label}
    disabled={disabled}
    onClick={onClick}
    className="flex size-11 shrink-0 items-center justify-center rounded-md border border-line-strong bg-surface text-ink hover:bg-tile disabled:opacity-30 md:size-9"
  >
    {children}
  </button>
);

function ColumnsEditor({ src, cfg, set }: EditorProps) {
  const cols = editorColumns(src, cfg);
  const label = (k: string) => src.fields.find((f) => f.key === k)?.label ?? k;
  const write = (next: typeof cols) => set({ ...cfg, columns: next });
  return (
    <Section title="Колонки" hint="Отметьте, что показывать, и выстройте порядок кнопками.">
      <ul className="flex flex-col gap-1">
        {cols.map((c, i) => (
          <li key={c.key} className="flex items-center gap-2">
            <Checkbox
              className="min-h-11 flex-1 items-center md:min-h-9"
              label={label(c.key)}
              checked={c.visible}
              onChange={(e) =>
                write(cols.map((x) => (x.key === c.key ? { ...x, visible: e.target.checked } : x)))
              }
            />
            <IconBtn
              label={`${label(c.key)}: выше`}
              disabled={i === 0}
              onClick={() => write(moveColumn(cols, i, -1))}
            >
              <ArrowUp className="size-4" />
            </IconBtn>
            <IconBtn
              label={`${label(c.key)}: ниже`}
              disabled={i === cols.length - 1}
              onClick={() => write(moveColumn(cols, i, 1))}
            >
              <ArrowDown className="size-4" />
            </IconBtn>
          </li>
        ))}
      </ul>
    </Section>
  );
}

function FilterValue({ f, flt, set }: { f: SourceField; flt: ViewFilter; set: (n: ViewFilter) => void }) {
  if (flt.op === 'empty' || flt.op === 'notempty') return null;
  const type = f.type === 'number' ? 'number' : f.type === 'date' ? 'date' : 'text';
  const val = (v: unknown) => (v == null ? '' : String(v));
  const conv = (s: string) => (f.type === 'number' && s !== '' && Number.isFinite(Number(s)) ? Number(s) : s);
  if (f.type === 'bool')
    return (
      <Select
        className={ctl}
        aria-label="Значение"
        value={flt.value === false ? 'no' : 'yes'}
        onChange={(e) => set({ ...flt, value: e.target.value === 'yes' })}
      >
        <option value="yes">да</option>
        <option value="no">нет</option>
      </Select>
    );
  if (f.type === 'enum' && flt.op === 'in')
    return (
      <div className="flex flex-col gap-0.5">
        {(f.options ?? []).map((o) => (
          <Checkbox
            key={o.value}
            className="min-h-11 items-center md:min-h-8"
            label={o.label}
            checked={flt.values?.includes(o.value) ?? false}
            onChange={(e) => {
              const cur = new Set((flt.values ?? []).map(String));
              if (e.target.checked) cur.add(o.value);
              else cur.delete(o.value);
              set({ ...flt, values: [...cur] });
            }}
          />
        ))}
      </div>
    );
  if (f.type === 'enum')
    return (
      <Select
        className={ctl}
        aria-label="Значение"
        value={val(flt.value)}
        onChange={(e) => set({ ...flt, value: e.target.value })}
      >
        <option value="">— выберите —</option>
        {(f.options ?? []).map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </Select>
    );
  return (
    <div className="flex flex-col gap-2 sm:flex-row">
      <Input
        className={ctl}
        type={type}
        aria-label={flt.op === 'between' ? 'От' : 'Значение'}
        value={val(flt.value)}
        onChange={(e) => set({ ...flt, value: conv(e.target.value) })}
      />
      {flt.op === 'between' ? (
        <Input
          className={ctl}
          type={type}
          aria-label="До"
          value={val(flt.value2)}
          onChange={(e) => set({ ...flt, value2: conv(e.target.value) })}
        />
      ) : null}
    </div>
  );
}

function FiltersEditor({ src, cfg, set }: EditorProps) {
  const field = (k: string) => src.fields.find((f) => f.key === k);
  const patch = (i: number, next: ViewFilter) =>
    set({ ...cfg, filters: cfg.filters.map((x, j) => (j === i ? next : x)) });
  return (
    <Section title="Отбор" hint="Записи, не подходящие под условия, не показываются и не попадают в итоги.">
      <ul className="flex flex-col gap-3">
        {cfg.filters.map((flt, i) => {
          const f = field(flt.field);
          return (
            // biome-ignore lint/suspicious/noArrayIndexKey: условия отбора без собственного ключа
            <li key={i} className="flex flex-col gap-2 rounded-md border border-line p-2">
              <div className="flex items-center gap-2">
                <Select
                  className={ctl}
                  aria-label="Поле"
                  value={flt.field}
                  onChange={(e) => {
                    const nf = field(e.target.value);
                    if (nf) patch(i, newFilter(nf));
                  }}
                >
                  {src.fields.map((x) => (
                    <option key={x.key} value={x.key}>
                      {x.label}
                    </option>
                  ))}
                </Select>
                <IconBtn
                  label="Убрать условие"
                  onClick={() => set({ ...cfg, filters: cfg.filters.filter((_, j) => j !== i) })}
                >
                  <X className="size-4" />
                </IconBtn>
              </div>
              {f ? (
                <>
                  <Select
                    className={ctl}
                    aria-label="Условие"
                    value={flt.op}
                    onChange={(e) => patch(i, { ...flt, op: e.target.value as FilterOp, values: undefined })}
                  >
                    {opsFor(f).map((op) => (
                      <option key={op} value={op}>
                        {opTitle(op)}
                      </option>
                    ))}
                  </Select>
                  <FilterValue f={f} flt={flt} set={(n) => patch(i, n)} />
                </>
              ) : null}
            </li>
          );
        })}
      </ul>
      <div>
        <Button
          size="sm"
          icon={<Plus className="size-4" />}
          onClick={() => {
            const first = src.fields[0];
            if (first) set({ ...cfg, filters: [...cfg.filters, newFilter(first)] });
          }}
        >
          Добавить условие
        </Button>
      </div>
    </Section>
  );
}

function GroupSortEditor({ src, cfg, set }: EditorProps) {
  const groupable = src.fields.filter((f) => f.groupable);
  const setGroup = (i: number, key: string) => {
    const next = cfg.groupBy.slice();
    if (key) next[i] = key;
    else next.splice(i, 1);
    set({ ...cfg, groupBy: [...new Set(next)].slice(0, 2) });
  };
  const sort = cfg.sort;
  const setSort = (i: number, patch: Partial<{ field: string; dir: 'asc' | 'desc' }> | null) => {
    const next = sort.slice();
    if (patch === null) next.splice(i, 1);
    else next[i] = { field: patch.field ?? next[i]?.field ?? '', dir: patch.dir ?? next[i]?.dir ?? 'asc' };
    set({ ...cfg, sort: next.filter((s) => s.field).slice(0, 3) });
  };
  return (
    <>
      <Section title="Группировка" hint="До двух полей. В группах считаются свои итоги.">
        {[0, 1].slice(0, Math.min(2, cfg.groupBy.length + 1)).map((i) => (
          <Select
            key={i}
            className={ctl}
            aria-label={`Группировать по, уровень ${i + 1}`}
            value={cfg.groupBy[i] ?? ''}
            onChange={(e) => setGroup(i, e.target.value)}
          >
            <option value="">{i === 0 ? 'Без группировки' : 'Без второго уровня'}</option>
            {groupable.map((f) => (
              <option key={f.key} value={f.key}>
                {f.label}
              </option>
            ))}
          </Select>
        ))}
      </Section>
      <Section title="Сортировка" hint="До трёх полей по порядку.">
        {[...sort, { field: '', dir: 'asc' as const }].slice(0, 3).map((s, i) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: уровни сортировки по порядку
          <div key={i} className="flex gap-2">
            <Select
              className={ctl}
              aria-label={`Сортировать по, ${i + 1}`}
              value={s.field}
              onChange={(e) => setSort(i, e.target.value ? { field: e.target.value } : null)}
            >
              <option value="">{i === 0 ? 'Порядок источника' : '— ещё одно поле —'}</option>
              {src.fields.map((f) => (
                <option key={f.key} value={f.key}>
                  {f.label}
                </option>
              ))}
            </Select>
            {s.field ? (
              <Select
                className={`${ctl} max-w-40`}
                aria-label="Направление"
                value={s.dir}
                onChange={(e) => setSort(i, { dir: e.target.value as 'asc' | 'desc' })}
              >
                <option value="asc">по возрастанию</option>
                <option value="desc">по убыванию</option>
              </Select>
            ) : null}
          </div>
        ))}
      </Section>
    </>
  );
}

function TotalsEditor({ src, cfg, set }: EditorProps) {
  const has = (field: string, fn: AggFn) => cfg.totals.some((t) => t.field === field && t.fn === fn);
  const toggle = (field: string, fn: AggFn, on: boolean) =>
    set({
      ...cfg,
      totals: on
        ? [...cfg.totals, { field, fn }].slice(0, 10)
        : cfg.totals.filter((t) => !(t.field === field && t.fn === fn)),
    });
  const fields = src.fields.filter((f) => f.aggregatable || f.type === 'number' || f.type === 'date');
  return (
    <Section title="Итоги" hint="Считаются по всей выборке и в каждой группе. До десяти итогов.">
      {fields.length ? (
        <ul className="flex flex-col gap-2">
          {fields.map((f) => (
            <li key={f.key} className="flex flex-col gap-0.5">
              <span className="text-[13px]">{f.label}</span>
              <span className="flex flex-wrap gap-x-4">
                {aggFnsFor(f).map((fn) => (
                  <Checkbox
                    key={fn}
                    className="min-h-11 items-center md:min-h-8"
                    label={AGG_FNS[fn]}
                    checked={has(f.key, fn)}
                    onChange={(e) => toggle(f.key, fn, e.target.checked)}
                  />
                ))}
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-[13px] text-ink-2">В этом источнике нет числовых полей для итогов.</p>
      )}
    </Section>
  );
}

function KindEditor({ src, cfg, set }: EditorProps) {
  const kinds = viewKindsFor(src);
  const enums = src.fields.filter((f) => f.type === 'enum');
  const groupable = src.fields.filter((f) => f.groupable);
  const nums = src.fields.filter((f) => f.type === 'number');
  const chart = cfg.chart ?? {
    x: groupable[0]?.key ?? '',
    y: null,
    fn: 'count' as AggFn,
    kind: 'bar' as const,
  };
  return (
    <Section title="Вид" hint="Карта появится, когда у записей будут координаты.">
      <Select
        className={ctl}
        aria-label="Вид"
        value={cfg.viewKind}
        onChange={(e) => set({ ...cfg, viewKind: e.target.value as ViewConfig['viewKind'] })}
      >
        {kinds.map((k) => (
          <option key={k} value={k}>
            {VIEW_KINDS[k]}
          </option>
        ))}
      </Select>
      {cfg.viewKind === 'board' ? (
        <Field label="Колонки доски по полю">
          <Select
            className={ctl}
            value={cfg.boardField ?? enums[0]?.key ?? ''}
            onChange={(e) => set({ ...cfg, boardField: e.target.value })}
          >
            {enums.map((f) => (
              <option key={f.key} value={f.key}>
                {f.label}
              </option>
            ))}
          </Select>
        </Field>
      ) : null}
      {cfg.viewKind === 'chart' ? (
        <div className="flex flex-col gap-2">
          <Field label="По оси X (группа)">
            <Select
              className={ctl}
              value={chart.x}
              onChange={(e) => set({ ...cfg, chart: { ...chart, x: e.target.value } })}
            >
              {groupable.map((f) => (
                <option key={f.key} value={f.key}>
                  {f.label}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Что считать">
            <div className="flex gap-2">
              <Select
                className={ctl}
                value={chart.fn}
                onChange={(e) => set({ ...cfg, chart: { ...chart, fn: e.target.value as AggFn } })}
              >
                {(Object.keys(AGG_FNS) as AggFn[]).map((fn) => (
                  <option key={fn} value={fn}>
                    {AGG_FNS[fn]}
                  </option>
                ))}
              </Select>
              {chart.fn !== 'count' ? (
                <Select
                  className={ctl}
                  aria-label="Поле значения"
                  value={chart.y ?? ''}
                  onChange={(e) => set({ ...cfg, chart: { ...chart, y: e.target.value || null } })}
                >
                  <option value="">— поле —</option>
                  {nums.map((f) => (
                    <option key={f.key} value={f.key}>
                      {f.label}
                    </option>
                  ))}
                </Select>
              ) : null}
            </div>
          </Field>
          <Field label="Рисунок">
            <Select
              className={ctl}
              value={chart.kind}
              onChange={(e) =>
                set({ ...cfg, chart: { ...chart, kind: e.target.value as 'bar' | 'line' | 'pie' } })
              }
            >
              <option value="bar">Столбцы</option>
              <option value="line">Линия</option>
              <option value="pie">Доли</option>
            </Select>
          </Field>
        </div>
      ) : null}
    </Section>
  );
}

function PeriodEditor({ src, cfg, set }: EditorProps) {
  if (!src.dateField) return null;
  const dates = src.fields.filter((f) => f.type === 'date');
  const p = cfg.period ?? { field: src.dateField, from: null, to: null };
  return (
    <Section title="Период" hint="Записи с датой вне периода не показываются.">
      <Select
        className={ctl}
        aria-label="Дата для периода"
        value={p.field}
        onChange={(e) => set({ ...cfg, period: { ...p, field: e.target.value } })}
      >
        {dates.map((f) => (
          <option key={f.key} value={f.key}>
            {f.label}
          </option>
        ))}
      </Select>
      <div className="flex gap-2">
        <Input
          className={ctl}
          type="date"
          aria-label="С"
          value={p.from ?? ''}
          onChange={(e) => set({ ...cfg, period: { ...p, from: e.target.value || null } })}
        />
        <Input
          className={ctl}
          type="date"
          aria-label="По"
          value={p.to ?? ''}
          onChange={(e) => set({ ...cfg, period: { ...p, to: e.target.value || null } })}
        />
      </div>
    </Section>
  );
}

interface EditorProps {
  src: SourceInfo;
  cfg: ViewConfig;
  set: (c: ViewConfig) => void;
}

export function ViewEditor({
  src,
  config,
  onChange,
}: {
  src: SourceInfo;
  config: ViewConfig;
  onChange: (c: ViewConfig) => void;
}) {
  const p = { src, cfg: config, set: onChange };
  return (
    <div className="flex flex-col gap-4">
      <KindEditor {...p} />
      <ColumnsEditor {...p} />
      <FiltersEditor {...p} />
      <PeriodEditor {...p} />
      <GroupSortEditor {...p} />
      <TotalsEditor {...p} />
    </div>
  );
}
